const QualityAssuranceUnit5Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-violet-900 mb-3 border-b-2 border-slate-200 pb-2">Unit V: Calibration, Validation & Warehousing</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            In the pharmaceutical industry, producing a quality product once is not enough; the process must be perfectly repeatable. This unit covers the fundamental concepts of Calibration and Validation to ensure equipment and processes consistently perform as expected, along with Good Warehousing Practices for safe material management.
        </p>
    </div>

    <!-- 2. Definitions: Calibration, Qualification, Validation -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-violet-800 mb-4 border-b border-slate-100 pb-2">1. Calibration, Qualification and Validation</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5 text-[13px] text-slate-700">
            <div class="bg-violet-50 p-4 rounded-lg border border-violet-200 shadow-sm">
                <h4 class="font-bold text-violet-900 text-[15px] mb-2">A. Calibration</h4>
                <p class="text-[12px] text-slate-600">
                    The set of operations that establish, under specified conditions, the relationship between values indicated by an instrument and the corresponding known values of a reference standard.
                </p>
                <p class="mt-2 text-[11px] font-bold text-violet-800 italic">"Ensuring the scale reads correctly."</p>
            </div>
            
            <div class="bg-fuchsia-50 p-4 rounded-lg border border-fuchsia-200 shadow-sm">
                <h4 class="font-bold text-fuchsia-900 text-[15px] mb-2">B. Qualification</h4>
                <p class="text-[12px] text-slate-600">
                    Action of proving that any premises, systems, and equipment work correctly and actually lead to the expected results. (Usually divided into DQ, IQ, OQ, PQ).
                </p>
                <p class="mt-2 text-[11px] font-bold text-fuchsia-800 italic">"Ensuring the machine is installed & works right."</p>
            </div>

            <div class="bg-purple-50 p-4 rounded-lg border border-purple-200 shadow-sm">
                <h4 class="font-bold text-purple-900 text-[15px] mb-2">C. Validation</h4>
                <p class="text-[12px] text-slate-600">
                    Establishing documented evidence which provides a high degree of assurance that a specific process will consistently produce a product meeting its pre-determined specifications.
                </p>
                <p class="mt-2 text-[11px] font-bold text-purple-800 italic">"Ensuring the entire process makes the right drug."</p>
            </div>
        </div>

        <!-- Types of Validation -->
        <h4 class="font-bold text-violet-900 text-[15px] mb-2">Types of Process Validation:</h4>
        <div class="overflow-x-auto rounded-lg border border-slate-200 mb-5">
            <table class="min-w-full bg-white text-[12px] text-left">
                <thead class="bg-slate-100 text-slate-700">
                    <tr>
                        <th class="px-4 py-2.5 font-bold w-1/4">Type</th>
                        <th class="px-4 py-2.5 font-bold">Description & Timing</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr>
                        <td class="px-4 py-3 font-bold text-violet-800">Prospective Validation</td>
                        <td class="px-4 py-3">Conducted <strong>before</strong> the distribution of a new product or a product made under a revised manufacturing process. (The most common type).</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-violet-800">Concurrent Validation</td>
                        <td class="px-4 py-3">Carried out <strong>during</strong> routine production. Used when data from replicate production runs are unavailable (e.g., orphan drugs made rarely).</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-violet-800">Retrospective Validation</td>
                        <td class="px-4 py-3">Based entirely on historical data analysis. Used only for well-established products manufactured using unchanged processes.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-violet-800">Revalidation</td>
                        <td class="px-4 py-3">Repeated validation to ensure changes in equipment, raw material, or process have not adversely affected product quality.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Validation Master Plan -->
        <div class="bg-slate-50 p-4 rounded border border-slate-200 text-[13px] text-slate-700">
            <h4 class="font-bold text-violet-900 mb-1">Validation Master Plan (VMP)</h4>
            <p class="text-[12px] text-slate-600">
                A high-level document that summarizes the manufacturer's overall philosophy, intentions, and approach to be used for establishing performance adequacy. It details <em>who</em> does <em>what</em>, <em>when</em>, and <em>how</em> validation activities will be conducted across the entire facility.
            </p>
        </div>
    </div>

    <!-- 3. Equipment Calibration & Qualification -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-violet-800 mb-4 border-b border-slate-100 pb-2">2. Calibration & Qualification of Specific Instruments</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- pH Meter -->
            <div class="border border-violet-200 p-4 rounded-lg bg-violet-50">
                <h4 class="text-[15px] font-bold text-violet-900 mb-2">A. Calibration of pH Meter</h4>
                <p class="text-[12px] text-slate-700 mb-2">A pH meter must be calibrated daily before use to ensure accurate acidity/alkalinity readings.</p>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Standard Buffers:</strong> Standardized using freshly prepared or certified buffer solutions of known pH (usually pH 4.0, 7.0, and 9.2).</li>
                    <li><strong>Procedure:</strong> The electrode is rinsed, immersed in pH 7.0 buffer first to set the 'Zero' or 'Asymmetry' point. Then, rinsed and immersed in pH 4.0 or 9.2 to set the 'Slope'.</li>
                    <li>Temperature compensation must be accounted for during calibration.</li>
                </ul>
            </div>

            <!-- UV-Vis Spectrophotometer -->
            <div class="border border-fuchsia-200 p-4 rounded-lg bg-fuchsia-50">
                <h4 class="text-[15px] font-bold text-fuchsia-900 mb-2">B. Qualification of UV-Vis Spectrophotometer</h4>
                <p class="text-[12px] text-slate-700 mb-2">Crucial for quantitative assay testing. Requires periodic verification of multiple parameters:</p>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Wavelength Accuracy:</strong> Verified using a holmium oxide glass filter or potassium dichromate solution, which have sharp, known absorption peaks.</li>
                    <li><strong>Photometric Accuracy:</strong> Verified by measuring the absorbance of a standard potassium dichromate solution at specific wavelengths.</li>
                    <li><strong>Stray Light Limit:</strong> Checked using a 1.2% w/v KCl solution.</li>
                    <li><strong>Resolution:</strong> Checked by measuring the ratio of absorbance of a Toluene solution in Hexane.</li>
                </ul>
            </div>
        </div>
    </div>

    <!-- 4. Analytical Method Validation -->
    <div class="bg-slate-800 text-white p-6 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black mb-3">3. General Principles of Analytical Method Validation</h3>
        <p class="text-[13px] text-slate-300 mb-4">
            Analytical method validation establishes that the performance characteristics of an analytical procedure (like HPLC assay) are suitable for its intended purpose. The ICH Q2(R1) guidelines define the following key parameters:
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-indigo-300 mb-1">1. Accuracy</p>
                <p class="text-slate-400">The closeness of agreement between the measured value and the true/accepted reference value. (Determined by recovery studies).</p>
            </div>
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-blue-300 mb-1">2. Precision</p>
                <p class="text-slate-400">The degree of agreement among individual test results when applied repeatedly to multiple samplings. (Repeatability, Intermediate Precision, Reproducibility).</p>
            </div>
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-teal-300 mb-1">3. Specificity</p>
                <p class="text-slate-400">The ability to assess unequivocally the exact analyte in the presence of expected components (impurities, degradants, excipients).</p>
            </div>
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-pink-300 mb-1">4. Linearity</p>
                <p class="text-slate-400">The ability to obtain test results which are directly proportional to the concentration of analyte within a given range.</p>
            </div>
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-yellow-300 mb-1">5. Range</p>
                <p class="text-slate-400">The interval between the upper and lower concentration levels of analyte where the method has demonstrated acceptable accuracy, precision, and linearity.</p>
            </div>
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-red-300 mb-1">6. LOD (Limit of Detection)</p>
                <p class="text-slate-400">The lowest amount of analyte in a sample that can be <em>detected</em> but not necessarily quantified.</p>
            </div>
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-green-300 mb-1">7. LOQ (Limit of Quantitation)</p>
                <p class="text-slate-400">The lowest amount of analyte in a sample that can be <em>quantified</em> with acceptable precision and accuracy.</p>
            </div>
            <div class="bg-slate-700 p-3 rounded border border-slate-600">
                <p class="font-bold text-orange-300 mb-1">8. Robustness</p>
                <p class="text-slate-400">A measure of the method's capacity to remain unaffected by small, deliberate variations in parameters (like slight changes in pH or flow rate).</p>
            </div>
        </div>
    </div>

    <!-- 5. Warehousing -->
    <div class="bg-white p-6 mt-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-violet-800 mb-3 border-b border-slate-100 pb-2">4. Warehousing & Materials Management</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
                <h4 class="text-[15px] font-bold text-violet-900 mb-2">Good Warehousing Practice</h4>
                <p class="text-[12px] text-slate-700 mb-2">Storage areas must be designed to ensure good storage conditions. In particular, they must be clean, dry, and maintained within acceptable temperature limits.</p>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Layout:</strong> Proper segregation of receiving, quarantine, approved, and rejected storage areas.</li>
                    <li><strong>Environment:</strong> Routine monitoring and mapping of temperature and humidity. Special cold storage (2°C to 8°C) mapped for cold chain products.</li>
                    <li><strong>Pest Control:</strong> A strict pest, rodent, and insect control program must be documented and active.</li>
                </ul>
            </div>
            <div>
                <h4 class="text-[15px] font-bold text-violet-900 mb-2">Materials Management</h4>
                <p class="text-[12px] text-slate-700 mb-2">Ensuring the right material is available at the right time, in the right quantity, and of the right quality.</p>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Inventory Control:</strong> Follows strict FEFO (First Expiry, First Out) or FIFO (First In, First Out) rules.</li>
                    <li><strong>Dispensing:</strong> Materials must be weighed or measured under specific, controlled conditions (often under Reverse Laminar Airflow booths) to prevent cross-contamination.</li>
                    <li><strong>Re-testing:</strong> Chemicals have a shelf-life. If stored for a long period, they must be sent back to QC for re-evaluation before use.</li>
                </ul>
            </div>
        </div>
    </div>

</div>
`;

export default QualityAssuranceUnit5Notes;